import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { MusicianCard } from '@/components/MusicianCard';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlass, MusicNote } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [musicians, setMusicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedGenre, search]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedGenre && selectedGenre !== 'all') params.append('genre', selectedGenre);
      if (search) params.append('search', search);

      const [musiciansRes, genresRes] = await Promise.all([
        axios.get(`${API}/discover/musicians?${params}`),
        axios.get(`${API}/genres`),
      ]);
      
      setMusicians(musiciansRes.data);
      setGenres(genresRes.data);
    } catch (error) {
      console.error('Failed to fetch musicians:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-4">Scopri nuovi talenti</h1>
          <p className="text-[#A1A1AA] max-w-2xl">
            Esplora artisti e band emergenti. Ascolta la loro musica e contattali per i tuoi eventi.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca artisti..."
              className="pl-10 bg-[#111] border-white/10"
              data-testid="discover-search-input"
            />
          </div>
          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-48 bg-[#111] border-white/10" data-testid="discover-genre-filter">
              <SelectValue placeholder="Tutti i generi" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10">
              <SelectItem value="all">Tutti i generi</SelectItem>
              {genres.map((genre) => (
                <SelectItem key={genre} value={genre}>{genre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Genre Tags */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-8"
        >
          <button
            onClick={() => setSelectedGenre('all')}
            className={`genre-tag ${selectedGenre === 'all' ? 'active' : ''}`}
          >
            Tutti
          </button>
          {genres.slice(0, 8).map((genre) => (
            <button
              key={genre}
              onClick={() => setSelectedGenre(genre)}
              className={`genre-tag ${selectedGenre === genre ? 'active' : ''}`}
            >
              {genre}
            </button>
          ))}
        </motion.div>

        {/* Musicians Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : musicians.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {musicians.map((musician, index) => (
              <MusicianCard
                key={musician.id}
                musician={musician}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <MusicNote size={64} className="text-[#333] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Nessun artista trovato</p>
            {search && (
              <button
                onClick={() => { setSearch(''); setSelectedGenre('all'); }}
                className="text-[#CCFF00] hover:underline mt-2"
              >
                Resetta filtri
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
